import { ReactNode } from "react";
import { Bold, Underline, Languages } from "lucide-react";

/**
 * Parses strings containing `Kanji{Furigana}` patterns and maps them to HTML `<ruby>` elements.
 * Example input: "これは日本語{にほんご}の試験{しけん}です。"
 * Renders Kanji with Furigana cleanly aligned above it.
 */
export function renderFurigana(text: string): ReactNode {
  if (!text) return "";
  
  // Split on formatting tags (**bold**, <u>underline</u>, <b>bold</b>)
  const formattingRegex = /(\*\*[^*]+\*\*|<u>[^<]+<\/u>|<b>[^<]+<\/b>)/g;
  const parts = text.split(formattingRegex);
  
  return (
    <>
      {parts.map((part, idx) => {
        if (part.startsWith("**") && part.endsWith("**")) {
          return <strong key={idx}>{renderFurigana(part.slice(2, -2))}</strong>;
        }
        if (part.startsWith("<u>") && part.endsWith("</u>")) {
          return <u key={idx}>{renderFurigana(part.slice(3, -4))}</u>;
        }
        if (part.startsWith("<b>") && part.endsWith("</b>")) {
          return <strong key={idx}>{renderFurigana(part.slice(3, -4))}</strong>;
        }
        
        // For plain text, split on matches of Kanji{Furigana} or [Kanji]{Furigana}
        const subparts = part.split(/(\[[^\]]+\]\{[^\}]+\}|[a-zA-Z0-9\u4e00-\u9fff\u30a0-\u30ff\u30fc\uff10-\uff19\uff21-\uff3a\uff41-\uff5a\uff66-\uff9f]+\{[^\}]+\})/g);
        return (
          <span key={idx}>
            {subparts.map((subpart, subIdx) => {
              const bracketMatch = subpart.match(/^\[([^\]]+)\]\{([^\}]+)\}$/);
              if (bracketMatch) {
                return (
                  <ruby key={subIdx} className="ruby-aligned">
                    {bracketMatch[1]}
                    <rt className="text-[0.65em] tracking-normal select-none text-muted-foreground/90 font-sans">
                      {bracketMatch[2]}
                    </rt>
                  </ruby>
                );
              }
              const plainMatch = subpart.match(/^([a-zA-Z0-9\u4e00-\u9fff\u30a0-\u30ff\u30fc\uff10-\uff19\uff21-\uff3a\uff41-\uff5a\uff66-\uff9f]+)\{([^\}]+)\}$/);
              if (plainMatch) {
                return (
                  <ruby key={subIdx} className="ruby-aligned">
                    {plainMatch[1]}
                    <rt className="text-[0.65em] tracking-normal select-none text-muted-foreground/90 font-sans">
                      {plainMatch[2]}
                    </rt>
                  </ruby>
                );
              }
              return subpart;
            })}
          </span>
        );
      })}
    </>
  );
}

/**
 * Inserts formatting tags at current caret selection inside a HTML textarea or input.
 */
export function insertFormatting(
  textarea: HTMLTextAreaElement | HTMLInputElement | null,
  type: "bold" | "underline" | "furigana",
  value: string,
  onUpdate: (newVal: string) => void
) {
  if (!textarea) return;
  textarea.focus();
  const start = textarea.selectionStart ?? 0;
  const end = textarea.selectionEnd ?? 0;
  const selectedText = value.substring(start, end);

  if (type === "furigana") {
    const text = selectedText || "Kanji";
    const replacement = `${text}{Furigana}`;
    const newVal = value.substring(0, start) + replacement + value.substring(end);
    onUpdate(newVal);
    setTimeout(() => {
      textarea.selectionStart = start + text.length + 1; // skip {
      textarea.selectionEnd = start + text.length + 9; // length of 'Furigana'
      textarea.focus();
    }, 0);
    return;
  }

  let prefix = "";
  let suffix = "";
  let placeholder = "";

  if (type === "bold") {
    prefix = "**";
    suffix = "**";
    placeholder = "bold";
  } else if (type === "underline") {
    prefix = "<u>";
    suffix = "</u>";
    placeholder = "underline";
  }

  const textToWrap = selectedText || placeholder;
  const replacement = prefix + textToWrap + suffix;
  const newVal = value.substring(0, start) + replacement + value.substring(end);

  onUpdate(newVal);

  setTimeout(() => {
    textarea.selectionStart = start + prefix.length;
    textarea.selectionEnd = start + prefix.length + textToWrap.length;
    textarea.focus();
  }, 0);
}

interface TextFormattingToolbarProps {
  textareaRef: React.RefObject<HTMLTextAreaElement>;
  value: string;
  onChange: (val: string) => void;
}

export function TextFormattingToolbar({ textareaRef, value, onChange }: TextFormattingToolbarProps) {
  return (
    <div className="flex gap-1.5 items-center pb-1 border-b border-border/20 mb-1">
      <button
        type="button"
        className="h-6 w-6 rounded flex items-center justify-center hover:bg-secondary text-muted-foreground hover:text-foreground transition-all border border-transparent hover:border-border/30"
        title="Bold text (**bold**)"
        onClick={() => insertFormatting(textareaRef.current, "bold", value, onChange)}
      >
        <Bold size={12} className="stroke-[2.5]" />
      </button>
      <button
        type="button"
        className="h-6 w-6 rounded flex items-center justify-center hover:bg-secondary text-muted-foreground hover:text-foreground transition-all border border-transparent hover:border-border/30"
        title="Underline text (<u>underline</u>)"
        onClick={() => insertFormatting(textareaRef.current, "underline", value, onChange)}
      >
        <Underline size={12} className="stroke-[2.5]" />
      </button>
      <div className="h-3 w-[1px] bg-border/40 mx-0.5" />
      <button
        type="button"
        className="px-1.5 py-0.5 rounded text-[10px] flex items-center gap-1 hover:bg-secondary text-primary hover:text-primary/80 font-bold transition-all border border-transparent hover:border-border/30"
        title="Add Kanji{Furigana} helper"
        onClick={() => insertFormatting(textareaRef.current, "furigana", value, onChange)}
      >
        <Languages size={11} />
        <span>Kanji{`{Furigana}`}</span>
      </button>
    </div>
  );
}
